import { useLocation } from '@reach/router';
import React, { useRef } from 'react';
import { IconBrackets, IconCloud, IconEnter, IconHardware, IconLearn, IconPaperStandard, IconProgramming, IconSoftware } from '../Icons';

import classnames from 'classnames';
import {
  CLOUD_PATH,
  EXAMPLES_PATH,
  HARDWARE_PATH,
  LANGUAGE_REFERENCE_EN_PAGE,
  LANGUAGE_REFERENCE_PATH,
  LEARN_PATH,
  LIBRARY_GEN_PATH,
  LIBRARY_PAGE,
  MYCROPYTHON_PATH,
  PRODUCT_PATH,
  SIDEBAR_CLOUD,
  SIDEBAR_LIBRARY,
  SIDEBAR_MYCROPYTHON,
  SOFTWARE_PATH,
} from '../../utils/constants';
import './SearchBarHits.scss';

interface SearchBarHitProps {
  hit: any;
  saveRecentSearch: (val: any) => void;
  selectedPanelItem: number;
  index: number;
  setSelectedPanelSlug: (val: any) => void;
  setHasResource: (val: any) => void;
  hasResource: string | null;
}

export default function SearchBarHit({
  setHasResource,
  hit,
  saveRecentSearch,
  selectedPanelItem,
  index,
  setSelectedPanelSlug,
  hasResource,
}: SearchBarHitProps): React.ReactElement {
  const cardRef = useRef(null);
  const location = useLocation();

  const getIcon = (slug: string) => {
    switch (slug) {
      case PRODUCT_PATH:
      case HARDWARE_PATH:
        return <IconHardware />;
      case SOFTWARE_PATH:
        return <IconSoftware />;
      case CLOUD_PATH:
        return <IconCloud />;
      case MYCROPYTHON_PATH:
      case EXAMPLES_PATH:
        return <IconProgramming />;
      case LEARN_PATH:
        return <IconLearn />;
      default:
        if (hit.documentation_type === SIDEBAR_LIBRARY) return <IconBrackets />;
        else return <IconPaperStandard />; //Standard Icon
    }
  };

  const getDocumentationType = (documentationType: string) => {
    switch (documentationType) {
      case 'Arduino-cloud':
        return SIDEBAR_CLOUD;
      case 'Micropython-course':
        return SIDEBAR_MYCROPYTHON;
      case 'Library':
        return SIDEBAR_LIBRARY;
      default:
        return documentationType; //Standard Icon
    }
  };

  const getTitle = (title: string) => {
    switch (title) {
      case 'arduino-cloud':
        return SIDEBAR_CLOUD;
      case 'micropython':
        return SIDEBAR_MYCROPYTHON;
      default:
        return title;
    }
  };

  // Tutorial generic are broken, we need custom breadcrumbs.
  const isGeneric =
    hit.slug.split('/')[2] === 'generic' ||
    hit.slug.split('/')[2] === 'communication' ||
    hit.slug.split('/')[2] === 'nano-generic' ||
    hit.slug.split('/')[2] === 'projects' ||
    hit.slug.split('/')[2] === 'security';

  //Set the link to redirect after Enter press
  if (selectedPanelItem === index) {
    setSelectedPanelSlug(hit.slug);

    if (hit.pinout) {
      setHasResource(hit.slug);
    }
  }

  // ! Middle breadcrumbs are broken from algolia content - refactor this
  const words = hit.product_slug?.replaceAll('-', ' ').split(' ');
  if (words) {
    for (let i = 0; i < words.length; i++) {
      words[i] = words[i][0].toUpperCase() + words[i].substr(1) + ' ';
      if (words[i] === 'Mkr ') words[i] = 'MKR ';
      if (words[i] === 'Wan ') words[i] = 'WAN ';
      if (words[i] === 'Uno ') words[i] = 'UNO ';
      if (words[i] === 'Rgb ') words[i] = 'RGB ';
      if (words[i] === 'Wifi ') words[i] = 'WiFi ';
      if (words[i] === 'Ble ') words[i] = 'BLE ';
      if (words[i] === 'Smd ') words[i] = 'SMD ';
      if (words[i] === 'Smd') words[i] = 'SMD';
      if (words[i] === 'Iot ') words[i] = 'IoT ';
      if (words[i] === 'Nb ') words[i] = 'NB ';
      if (words[i] === 'Gnss ') words[i] = 'GNSS ';
      if (words[i] === 'Fox ') words[i] = 'FOX ';
      if (words[i] === 'Gsm ') words[i] = 'GSM ';
      if (words[i] === 'Can ') words[i] = 'CAN ';
      if (words[i] === 'Env ') words[i] = 'ENV ';
      if (words[i] === 'Eth ') words[i] = 'ETH ';
      if (words[i] === 'Gps ') words[i] = 'GPS ';
      if (words[i] === 'Imu ') words[i] = 'IMU ';
      if (words[i] === 'Mem ') words[i] = 'MEM ';
      if (words[i] === 'Sd ') words[i] = 'SD ';
      if (words[i] === 'Mem ') words[i] = 'MEM ';
      if (words[i] === 'Esp32 ') words[i] = 'ESP32 ';
      if (words[i] === 'Rp2040 ') words[i] = 'RP2040 ';
      if (words[i] === 'Me ') words[i] = 'ME ';
      if (words[i] === 'M1 ') words[i] = 'M1/';
      if (words[i] === 'Cat ') words[i] = 'Cat. ';
      if (words[i] === 'Rev3 ') words[i] = 'R3 ';
      if (words[i] === 'Yun ') words[i] = 'Yún ';
      if (words[i] === 'Giga ') words[i] = 'GIGA ';
      if (words[i] === 'Wisgate ') words[i] = 'WisGate ';
      if (words[i] === 'Micropython ') words[i] = 'MicroPython ';
      if (words[i] === 'Micropython') words[i] = 'MicroPython';
    }
    words.join(' ');
  }
  const product = words;

  const isLR = location.pathname.split('/')[1] === LANGUAGE_REFERENCE_PATH;

  const isLibrary = location.pathname.split('/')[1] === LIBRARY_GEN_PATH;

  function getHomeLinks(slug: string) {
    if (slug.split('/')[1] === 'en' || slug.split('/')[1] === 'de' || slug.split('/')[1] === 'pt') {
      return LANGUAGE_REFERENCE_EN_PAGE + hit.slug;
    } else if (hit.documentation_type === SIDEBAR_LIBRARY) {
      return LIBRARY_PAGE + hit.slug;
    } else return hit.slug;
  }

  function getLRLinks(slug: string) {
    if (slug.split('/')[1] === 'en' || hit.slug.split('/')[1] === 'de' || hit.slug.split('/')[1] === 'pt') {
      return LANGUAGE_REFERENCE_EN_PAGE + hit.slug;
    } else return hit.slug;
  }

  function getLibraryLinks() {
    if (hit.documentation_type === SIDEBAR_LIBRARY) return process.env.GATSBY_DOCS_URL + LIBRARY_GEN_PATH + '/' + hit.slug;
    else return hit.slug;
  }

  return (
    <>
      <a
        href={isLR ? getLRLinks(hit.slug) : isLibrary ? getLibraryLinks() : getHomeLinks(hit.slug)}
        ref={cardRef}
        onClick={() => saveRecentSearch(hit)}
        className={classnames('search-hits', {
          'search-hits--selected': selectedPanelItem === index && hasResource !== hit.slug,
        })}
      >
        <div className="search-hits__img">{hit.slug ? getIcon(hit.slug.split('/')[1]) : getIcon(null)}</div>
        <div className="search-hits__content">
          <div className="search-hits__title">{hit.title}</div>
          <div className="search-hits__description">{hit.description}</div>
          {hit.slug?.split('/')[1] !== PRODUCT_PATH && hit.slug?.split('/')[1] !== SOFTWARE_PATH && (
            <div className="search-hits__breadcrumbs">
              Home
              <span className="search-hits__breadcrumbs--separator">{' / '}</span>
              {hit.slug.substring(0, 11) === '/tutorials/' && (
                <>
                  <span>{isGeneric ? 'Tutorials' : 'Hardware'}</span>
                  <span className="search-hits__breadcrumbs--separator">{' / '}</span>
                  {product && (
                    <>
                      <span>{product}</span>
                      <span className="search-hits__breadcrumbs--separator">{' / '}</span>
                    </>
                  )}
                </>
              )}
              {(hit.documentation_type !== SIDEBAR_LIBRARY &&
                hit.breadcrumbObj?.map((breadcrumb, i) => (
                  <span key={breadcrumb.slug} className="search-hits__breadcrumbs--title">
                    {breadcrumb.title === '' ? getDocumentationType(hit.documentation_type) : getTitle(breadcrumb.title)}{' '}
                    <span className="search-hits__breadcrumbs--separator">{'/ '}</span>
                    {i === hit.breadcrumbObj.length - 1 && hit.title}
                  </span>
                ))) ||
                (hit.documentation_type !== SIDEBAR_LIBRARY && hit.title)}
              {hit.documentation_type === SIDEBAR_LIBRARY && (
                <span key={hit.slug} className="search-hits__breadcrumbs--title">
                  <span>Library</span>
                  <span className="search-hits__breadcrumbs--separator">{' / '}</span>
                  {hit.title}
                </span>
              )}
            </div>
          )}
          {(hit.slug?.split('/')[1] === PRODUCT_PATH || hit.slug?.split('/')[1] === SOFTWARE_PATH) && (
            <div className="search-hits__breadcrumbs">
              <span>
                Home
                <span className="search-hits__breadcrumbs--separator">{' / '}</span>
                {hit.slug?.split('/')[1] === PRODUCT_PATH ? 'Hardware' : 'Software'}
                <span className="search-hits__breadcrumbs--separator">{' / '}</span>
                {hit.title}
              </span>
            </div>
          )}
        </div>
        {selectedPanelItem === index && hasResource !== hit.slug && (
          <div className="search-hits__enter">
            <IconEnter />
          </div>
        )}
      </a>
    </>
  );
}
